"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  elapsedBetween,
  formatClock,
  formatHms,
  fromDecimalHours,
  type ListEntry,
  parseClockTime,
  parseDuration,
  parseIsoDay,
  SECONDS_PER_DAY,
  SECONDS_PER_HOUR,
  splitHms,
  sumDurations,
  toDecimalHours,
} from "@/lib/tool-kit/converters/time-calculator";

/** English strings. A translated content file overrides these through `ui`. */
export const TIME_CALCULATOR_STRINGS = {
  mode: "What do you want to work out?",
  modeAdd: "Add or subtract times",
  modeBetween: "Time between two clock times",
  modeSum: "Add up a list",
  modeDecimal: "Decimal hours",
  durationA: "First duration",
  durationB: "Second duration",
  durationHint:
    "h:mm or h:mm:ss (1:45:30), or with units (1h 45m 30s, 90 min).",
  operation: "Operation",
  add: "Add (+)",
  subtract: "Subtract (−)",
  startTime: "Start time",
  endTime: "End time",
  startDate: "Start date (optional)",
  endDate: "End date (optional)",
  datesHint:
    "Leave both dates blank for a span within one day or one night. Fill in both for longer spans.",
  listLabel: "Durations — one per line, or separated by semicolons",
  listHint:
    "Use h:mm, h:mm:ss or units. A bare number counts as hours. Start a line with a minus sign to subtract it.",
  direction: "Convert",
  toDecimal: "Hours and minutes → decimal hours",
  fromDecimal: "Decimal hours → hours and minutes",
  hmsInput: "Hours, minutes and seconds",
  hmsInputHint: "For example 7:45, 2:45:30 or 2h 45m.",
  decimalInput: "Decimal hours",
  decimalInputHint: "For example 7.75 or 2,5.",
  result: "Result",
  hms: "Hours:minutes:seconds",
  decimalHours: "Decimal hours",
  totalMinutes: "Total minutes",
  totalSeconds: "Total seconds",
  daysAndTime: "Days and time",
  daysValue: "one: {count} day + {time}|other: {count} days + {time}",
  working: "Working",
  toSeconds: "{hms} = {h} × {secPerHour} + {m} × 60 + {s} = {total} s",
  addSeconds: "{a} s + {b} s = {total} s",
  subtractSeconds: "{a} s − {b} s = {total} s",
  fromSeconds: "{total} s = {h} × {secPerHour} + {m} × 60 + {s} = {hms}",
  fromSecondsNegative:
    "{total} s = −({h} × {secPerHour} + {m} × 60 + {s}) = {hms}",
  sameDay: "{end} − {start} = {result}",
  overnightNote:
    "The end time is earlier than the start time, so the span runs past midnight and 24 hours are added.",
  overnight: "({end} + 24:00:00) − {start} = {result}",
  sameTimeNote:
    "Start and end are the same time, so the span is zero. For a full 24 hours, add the dates.",
  daysApart:
    "one: The dates are {count} day apart.|other: The dates are {count} days apart.",
  dated: "{days} × 24:00:00 + ({end} − {start}) = {result}",
  dayLengthNote:
    "Every day counts as exactly 24 hours, so across a daylight-saving change the clock-on-the-wall difference is an hour more or less.",
  entry: "Entry",
  readAs: "Read as",
  runningTotal: "Running total",
  notReadable: "not readable",
  listSummary: "one: {count} entry added up.|other: {count} entries added up.",
  listSkipped:
    "one: {count} entry could not be read and was left out — check the rows marked “not readable”.|other: {count} entries could not be read and were left out — check the rows marked “not readable”.",
  toDecimalWorking: "{hms} = {h} + {m} ÷ 60 + {s} ÷ {secPerHour} = {decimal} h",
  fromDecimalWorking: "{decimal} h × {secPerHour} = {exact} s",
  roundedNote: "Rounded to the nearest second: {seconds} s.",
  invalidDuration:
    "“{text}” isn't a duration this calculator can read. Use h:mm, h:mm:ss, or units such as 1h 30m.",
  invalidDecimal: "Enter decimal hours as a number, such as 7.75.",
  invalidClock: "Enter both a start time and an end time.",
  oneDateOnly: "Fill in both dates, or leave both blank.",
  invalidDate: "One of the dates isn't a real calendar date.",
  endBeforeStart: "The end comes before the start. Check the dates and times.",
  emptyList: "Enter at least one duration to add up.",
  enterPrompt: "Fill in the fields above to see the result.",
  copySummary: "{hms} = {decimal} h",
  copy: "Copy result",
  copied: "Copied!",
} as const;

type Strings = typeof TIME_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type Mode = "add" | "between" | "sum" | "decimal";
type Direction = "to-decimal" | "from-decimal";

interface Outcome {
  seconds: number;
  working: string[];
  entries?: ListEntry[];
}

type Computed = { outcome: Outcome } | { error: string } | null;

/** One line of working: a duration's h:mm:ss form and its count of seconds. */
function hmsTerms(seconds: number, fmt: Fmt) {
  const parts = splitHms(seconds);
  return {
    h: fmt(parts.hours),
    m: fmt(parts.minutes),
    s: fmt(parts.seconds),
    secPerHour: fmt(SECONDS_PER_HOUR),
    total: fmt(seconds),
    hms: formatHms(seconds),
  };
}

function backToHms(seconds: number, strings: Strings, fmt: Fmt): string {
  return t(
    seconds < 0 ? strings.fromSecondsNegative : strings.fromSeconds,
    hmsTerms(seconds, fmt)
  );
}

function computeAdd(
  aText: string,
  bText: string,
  operation: "add" | "subtract",
  strings: Strings,
  fmt: Fmt
): Computed {
  if (aText.trim() === "" || bText.trim() === "") {
    return null;
  }
  const a = parseDuration(aText);
  const b = parseDuration(bText);
  if (a === null || b === null) {
    return {
      error: t(strings.invalidDuration, { text: a === null ? aText : bText }),
    };
  }
  const seconds = operation === "add" ? a + b : a - b;
  const combine =
    operation === "add" ? strings.addSeconds : strings.subtractSeconds;
  return {
    outcome: {
      seconds,
      working: [
        t(strings.toSeconds, hmsTerms(a, fmt)),
        t(strings.toSeconds, hmsTerms(b, fmt)),
        t(combine, { a: fmt(a), b: fmt(b), total: fmt(seconds) }),
        backToHms(seconds, strings, fmt),
      ],
    },
  };
}

interface BetweenInput {
  start: string;
  end: string;
  startDate: string;
  endDate: string;
}

function readDay(text: string): number | null | "invalid" {
  if (text.trim() === "") {
    return null;
  }
  return parseIsoDay(text) ?? "invalid";
}

function computeBetween(
  input: BetweenInput,
  strings: Strings,
  fmt: Fmt,
  locale: string
): Computed {
  const start = parseClockTime(input.start);
  const end = parseClockTime(input.end);
  if (start === null || end === null) {
    return { error: strings.invalidClock };
  }
  const startDay = readDay(input.startDate);
  const endDay = readDay(input.endDate);
  if (startDay === "invalid" || endDay === "invalid") {
    return { error: strings.invalidDate };
  }
  const span = elapsedBetween(start, end, startDay, endDay);
  if (!span.ok) {
    return {
      error:
        span.error === "one-date"
          ? strings.oneDateOnly
          : strings.endBeforeStart,
    };
  }
  const values = {
    start: formatClock(start),
    end: formatClock(end),
    result: formatHms(span.seconds),
  };
  let working: string[];
  if (span.kind === "dated") {
    working = [
      tPlural(strings.daysApart, locale, span.days, { count: fmt(span.days) }),
      t(strings.dated, { ...values, days: fmt(span.days) }),
      strings.dayLengthNote,
    ];
  } else if (span.kind === "overnight") {
    working = [strings.overnightNote, t(strings.overnight, values)];
  } else {
    working = [t(strings.sameDay, values)];
    if (span.seconds === 0) {
      working.push(strings.sameTimeNote);
    }
  }
  return { outcome: { seconds: span.seconds, working } };
}

function computeSum(
  text: string,
  strings: Strings,
  fmt: Fmt,
  locale: string
): Computed {
  const sum = sumDurations(text);
  if (sum.entries.length === 0) {
    return { error: strings.emptyList };
  }
  const working = [
    tPlural(strings.listSummary, locale, sum.readable, {
      count: fmt(sum.readable),
    }),
  ];
  if (sum.unreadable > 0) {
    working.push(
      tPlural(strings.listSkipped, locale, sum.unreadable, {
        count: fmt(sum.unreadable),
      })
    );
  }
  return {
    outcome: { seconds: sum.total, working, entries: sum.entries },
  };
}

function computeDecimal(
  direction: Direction,
  text: string,
  strings: Strings,
  fmt: Fmt
): Computed {
  if (text.trim() === "") {
    return null;
  }
  const decimals = { maximumFractionDigits: 4 };
  if (direction === "to-decimal") {
    const seconds = parseDuration(text);
    if (seconds === null) {
      return { error: t(strings.invalidDuration, { text }) };
    }
    const terms = hmsTerms(seconds, fmt);
    const decimal = fmt(toDecimalHours(seconds), decimals);
    return {
      outcome: {
        seconds,
        working: [t(strings.toDecimalWorking, { ...terms, decimal })],
      },
    };
  }
  const hours = parseNumberInput(text);
  if (hours === null) {
    return { error: strings.invalidDecimal };
  }
  const { exactSeconds, seconds } = fromDecimalHours(hours);
  const working = [
    t(strings.fromDecimalWorking, {
      decimal: fmt(hours, decimals),
      secPerHour: fmt(SECONDS_PER_HOUR),
      exact: fmt(exactSeconds, decimals),
    }),
  ];
  if (exactSeconds !== seconds) {
    working.push(t(strings.roundedNote, { seconds: fmt(seconds) }));
  }
  working.push(backToHms(seconds, strings, fmt));
  return { outcome: { seconds, working } };
}

function ResultPanel({
  outcome,
  strings,
}: {
  outcome: Outcome;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const { seconds } = outcome;
  const days = Math.trunc(Math.abs(seconds) / SECONDS_PER_DAY);
  const remainder = Math.abs(seconds) % SECONDS_PER_DAY;
  const decimal = fmt(toDecimalHours(seconds), { maximumFractionDigits: 4 });
  const summary = t(strings.copySummary, { hms: formatHms(seconds), decimal });
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.result}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.hms} value={formatHms(seconds)} />
        <StatCard label={strings.decimalHours} value={decimal} />
        <StatCard
          label={strings.totalMinutes}
          value={fmt(seconds / 60, { maximumFractionDigits: 2 })}
        />
        <StatCard label={strings.totalSeconds} value={fmt(seconds)} />
        {days > 0 ? (
          <StatCard
            label={strings.daysAndTime}
            value={tPlural(strings.daysValue, locale, days, {
              count: fmt(days),
              time: formatHms(remainder),
            })}
          />
        ) : null}
      </StatGrid>
      <div className="space-y-2">
        <p className={LABEL}>{strings.working}</p>
        <ul className="space-y-1 font-mono text-foreground text-sm">
          {outcome.working.map((line, index) => (
            // Working lines can repeat (adding a duration to itself), so position is the key.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional lines
            <li key={index}>{line}</li>
          ))}
        </ul>
      </div>
      {outcome.entries ? (
        <DataTable
          head={[strings.entry, strings.readAs, strings.runningTotal]}
          rows={outcome.entries.map((entry) => [
            entry.text,
            entry.seconds === null
              ? strings.notReadable
              : formatHms(entry.seconds),
            formatHms(entry.runningTotal),
          ])}
        />
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={summary}
      />
    </section>
  );
}

export default function TimeCalculatorWidget() {
  const strings = useToolUi(TIME_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("add");
  const [aText, setAText] = useState("1:45:30");
  const [bText, setBText] = useState("2:30:45");
  const [operation, setOperation] = useState<"add" | "subtract">("add");
  const [between, setBetween] = useState<BetweenInput>({
    start: "22:30",
    end: "06:15",
    startDate: "",
    endDate: "",
  });
  const [listText, setListText] = useState("7:45\n8:15\n6:30\n7h 50m\n4.25");
  const [direction, setDirection] = useState<Direction>("to-decimal");
  const [hmsText, setHmsText] = useState("7:45");
  const [decimalText, setDecimalText] = useState("2.76");

  let computed: Computed = null;
  if (mode === "add") {
    computed = computeAdd(aText, bText, operation, strings, fmt);
  } else if (mode === "between") {
    computed = computeBetween(between, strings, fmt, locale);
  } else if (mode === "sum") {
    computed = computeSum(listText, strings, fmt, locale);
  } else {
    computed = computeDecimal(
      direction,
      direction === "to-decimal" ? hmsText : decimalText,
      strings,
      fmt
    );
  }

  const setField = (field: keyof BetweenInput) => (value: string) =>
    setBetween((current) => ({ ...current, [field]: value }));

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.mode}
        name="tc-mode"
        onChange={setMode}
        options={[
          { value: "add", label: strings.modeAdd },
          { value: "between", label: strings.modeBetween },
          { value: "sum", label: strings.modeSum },
          { value: "decimal", label: strings.modeDecimal },
        ]}
        value={mode}
      />

      {mode === "add" ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="tc-a"
              label={strings.durationA}
              mono
              onChange={setAText}
              value={aText}
            />
            <TextField
              id="tc-b"
              label={strings.durationB}
              mono
              onChange={setBText}
              value={bText}
            />
          </div>
          <p className={HINT}>{strings.durationHint}</p>
          <PillGroup
            label={strings.operation}
            name="tc-operation"
            onChange={setOperation}
            options={[
              { value: "add", label: strings.add },
              { value: "subtract", label: strings.subtract },
            ]}
            value={operation}
          />
        </div>
      ) : null}

      {mode === "between" ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="tc-start"
              label={strings.startTime}
              onChange={setField("start")}
              type="time"
              value={between.start}
            />
            <TextField
              id="tc-end"
              label={strings.endTime}
              onChange={setField("end")}
              type="time"
              value={between.end}
            />
            <TextField
              id="tc-start-date"
              label={strings.startDate}
              onChange={setField("startDate")}
              type="date"
              value={between.startDate}
            />
            <TextField
              id="tc-end-date"
              label={strings.endDate}
              onChange={setField("endDate")}
              type="date"
              value={between.endDate}
            />
          </div>
          <p className={HINT}>{strings.datesHint}</p>
        </div>
      ) : null}

      {mode === "sum" ? (
        <TextAreaField
          hint={strings.listHint}
          id="tc-list"
          label={strings.listLabel}
          onChange={setListText}
          rows={7}
          value={listText}
        />
      ) : null}

      {mode === "decimal" ? (
        <div className="space-y-4">
          <PillGroup
            label={strings.direction}
            name="tc-direction"
            onChange={setDirection}
            options={[
              { value: "to-decimal", label: strings.toDecimal },
              { value: "from-decimal", label: strings.fromDecimal },
            ]}
            value={direction}
          />
          {direction === "to-decimal" ? (
            <TextField
              hint={strings.hmsInputHint}
              id="tc-hms"
              label={strings.hmsInput}
              mono
              onChange={setHmsText}
              value={hmsText}
            />
          ) : (
            <TextField
              hint={strings.decimalInputHint}
              id="tc-decimal"
              label={strings.decimalInput}
              numeric
              onChange={setDecimalText}
              value={decimalText}
            />
          )}
        </div>
      ) : null}

      {computed !== null && "error" in computed ? (
        <ErrorNotice>{computed.error}</ErrorNotice>
      ) : null}
      {computed !== null && "outcome" in computed ? (
        <ResultPanel outcome={computed.outcome} strings={strings} />
      ) : null}
      {computed === null ? <Notice>{strings.enterPrompt}</Notice> : null}
    </div>
  );
}
